/**
 * shared/theme.js
 * Single source of truth for the design system used across all apps.
 * src/theme.js re-exports this file for the customer app.
 */
const theme = {
  colors: {
    primary: "#1A1F2E",
    primaryLight: "#252C3F",
    primaryMid: "#2E3650",
    primaryGlow: "rgba(26,31,46,0.06)",
    accent: "#F59E0B",
    accentLight: "#FDE68A",
    accentDark: "#D97706",
    accentSubtle: "rgba(245,158,11,0.10)",
    accentBorder: "rgba(245,158,11,0.25)",
    success: "#10B981",
    successSubtle: "rgba(16,185,129,0.10)",
    danger: "#EF4444",
    dangerSubtle: "rgba(239,68,68,0.08)",
    info: "#3B82F6",
    infoSubtle: "rgba(59,130,246,0.08)",
    purple: "#8B5CF6",
    purpleSubtle: "rgba(139,92,246,0.08)",
    bg: "#F6F7F9",
    bgCard: "#FAFAFA",
    card: "#FFFFFF",
    border: "#E5E7EB",
    borderStrong: "#CBD5E1",
    textDark: "#0F172A",
    textMid: "#374151",
    textLight: "#6B7280",
    textMuted: "#9CA3AF",
    textInverse: "#FFFFFF",
    disabled: "#CBD5E1",
    loginSubtitle: "#94A3B8",
    statusPending: "#F59E0B",
    statusPacked: "#3B82F6",
    statusDelivered: "#10B981",
    statusCancelled: "#EF4444",
  },
  radius: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "20px",
    "2xl": "24px",
    "3xl": "28px",
    pill: "999px",
  },
  shadow: {
    xs: "0 1px 2px rgba(15,23,42,0.04)",
    sm: "0 1px 4px rgba(15,23,42,0.06)",
    card: "0 1px 2px rgba(15,23,42,0.05), 0 0 0 1px rgba(15,23,42,0.06)",
    cardHover: "0 8px 20px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.08)",
    nav: "0 -4px 18px rgba(15,23,42,0.14), 0 -1px 4px rgba(15,23,42,0.08)",
    floating: "0 10px 28px rgba(15,23,42,0.18), 0 2px 8px rgba(15,23,42,0.10)",
    header: "0 1px 0 rgba(0,0,0,0.08)",
    input: "0 1px 2px rgba(15,23,42,0.05)",
    accent: "0 2px 8px rgba(245,158,11,0.24)",
    accentLg: "0 6px 18px rgba(245,158,11,0.22)",
    success: "0 2px 8px rgba(16,185,129,0.22)",
    danger: "0 2px 8px rgba(239,68,68,0.20)",
    inner: "inset 0 1px 3px rgba(15,23,42,0.08)",
  },
  spacing: {
    bottomNav: "calc(110px + env(safe-area-inset-bottom, 0px))",
    page: "16px",
    pageLg: "20px",
  },
  font: {
    family: "'Inter', sans-serif",
  },
  transition: {
    fast: "0.12s ease",
    base: "0.2s ease",
    slow: "0.28s cubic-bezier(0.4, 0, 0.2, 1)",
    spring: "0.38s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    bounce: "0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
};

export const getCategoryBg = (category, isHover = false) => {
  const map = {
    "Chocolates & Candies": { base: "#FDF4FF", hover: "#FCE7F3" }, // Soft Pink
    "Baby Care & Female Hygiene": { base: "#FCE7F3", hover: "#FBCFE8" }, // Soft Pink
    "Snacks & Namkeen": { base: "#FFEDD5", hover: "#FED7AA" }, // Soft Orange
    "Tea, Coffee & Cold Drinks": { base: "#FFEDD5", hover: "#FED7AA" }, // Soft Orange
    "Chicken, Meat, Seafood": { base: "#FFEDD5", hover: "#FED7AA" }, // Soft Orange
    "Home & Kitchen Essentials": { base: "#F3F4F6", hover: "#E5E7EB" }, // Soft Gray
    "Biscuits & Cookies": { base: "#E0F2FE", hover: "#BAE6FD" }, // Soft Blue
    "Cleaning & Laundry": { base: "#E0F2FE", hover: "#BAE6FD" }, // Soft Blue
    "Dairy, Fresh & Frozen": { base: "#F3F4F6", hover: "#E5E7EB" }, // Soft Gray
    "Face & Oral Care": { base: "#FCE7F3", hover: "#FBCFE8" }, // Soft Pink
    "Foodgrains, Oil & Masala": { base: "#FEF9C3", hover: "#FEF08A" }, // Soft Yellow
    "Spices & Masala": { base: "#FEF9C3", hover: "#FEF08A" }, // Soft Yellow
    "Hair & Body Care": { base: "#DCFCE7", hover: "#BBF7D0" }, // Soft Green
    "Hair Colour & Beauty": { base: "#DCFCE7", hover: "#BBF7D0" }, // Soft Green
    "Health & Wellness": { base: "#DCFCE7", hover: "#BBF7D0" }, // Soft Green
    "Fruits & Vegetables": { base: "#DCFCE7", hover: "#BBF7D0" }, // Soft Green
    "Instant Packaged Food": { base: "#FEF9C3", hover: "#FEF08A" }, // Soft Yellow
    "Paper Goods & Disposables": { base: "#E0F2FE", hover: "#BAE6FD" }, // Soft Blue
    "Cakes & Bakery": { base: "#FCE7F3", hover: "#FBCFE8" }, // Soft Pink
  };
  
  const defaultColors = { base: "#F6F7F9", hover: "#F8FAFC" };
  const colors = map[category] || defaultColors;
  return isHover ? colors.hover : colors.base;
};

export default theme;
